import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { ScrollText, Plus, Edit, Trash2, Save, X, Maximize2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

export default function RulesPage() {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", order: 0, image: "", images: [] });
  const [selectedImage, setSelectedImage] = useState(null);

  const isAdmin = user?.role === "admin" || user?.role === "owner";

  const load = async () => {
    try {
      const { data } = await api.get("/rules");
      setRules(data || []);
    } catch {
      setRules([]);
    }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setCreating(true);
    setEditing(null);
    setForm({ title: "", body: "", order: rules.length + 1, image: "", images: [] });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/rules/${editing}`, form);
        toast.success("تم حفظ التعديلات بنجاح");
      } else {
        await api.post("/rules", form);
        toast.success("تمت إضافة القاعدة بنجاح");
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const startEdit = (r) => {
    const images = Array.isArray(r.images) && r.images.length ? r.images : (r.image ? [r.image] : []);
    setEditing(r.id);
    setForm({ title: r.title, body: r.body, order: r.order, image: images[0] || "", images });
    setCreating(false);
  };

  const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });

  const onImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const added = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) {
        toast.error(`الملف ${f.name} ليس صورة`);
        continue;
      }
      if (f.size > 2_000_000) {
        toast.error(`الصورة ${f.name} كبيرة (الحد 2MB)`);
        continue;
      }
      try {
    
        const data = await readAsDataUrl(f);
        if (typeof data === "string") added.push(data);
      } catch {
        toast.error(`فشل قراءة الصورة ${f.name}`);
      }
    }

    if (added.length) {
      setForm((p) => {
        const merged = [...(p.images || []), ...added];
        return { ...p, images: merged, image: merged[0] || "" };
      });
    }
    e.target.value = "";
  };

  const removeImageAt = (idx) => {
    setForm((p) => {
      const next = (p.images || []).filter((_, i) => i !== idx);
      return { ...p, images: next, image: next[0] || "" };
    });
  };

  const remove = async (id) => {
    if (!confirm("هل أنت متأكد من حذف هذه القاعدة؟")) return;
    try {
      await api.delete(`/rules/${id}`);
      toast.success("تم الحذف بنجاح");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" dir="rtl">
      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-gold-500/10 border border-gold-500/20 text-gold-500 text-xs font-bold mb-2">
            <ScrollText size={14} /> سياسات المنصة
          </div>
          <h1 className="font-display font-black text-3xl md:text-5xl text-white">
            القوانين العامة
          </h1>
          <p className="text-white/50 mt-1.5 text-sm">
            القواعد والشروط الأساسية التي تحكم السلوك العام ونزاهة المنافسة في منصة Rivals
          </p>
        </div>
        {isAdmin && !creating && !editing && (
          <button
            data-testid="add-rule-btn"
            onClick={startCreate}
            className="px-5 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 flex items-center gap-2 text-xs shadow-[0_0_15px_rgba(255,204,0,0.3)] transition"
          >
            <Plus size={16} /> إضافة قاعدة جديدة
          </button>
        )}
      </div>

      {/* ── Create / Edit Form Modal or Box ── */}
      {(creating || editing) && isAdmin && (
        <form onSubmit={save} className="bg-[#111113]/90 border border-white/15 rounded-3xl p-6 md:p-8 space-y-4 shadow-2xl animate-in fade-in duration-200" data-testid="rule-form">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <h2 className="font-display font-black text-lg text-white">
              {editing ? "تعديل القاعدة الحالية" : "إضافة قاعدة جديدة للمنصة"}
            </h2>
            <button
              type="button"
              onClick={() => { setEditing(null); setCreating(false); }}
              className="p-1 rounded-lg text-white/40 hover:text-white transition"
            >
              <X size={18} />
            </button>
          </div>

          <div>
            <label className="text-xs font-bold text-white/70 mb-1 block">عنوان القاعدة</label>
            <input
              data-testid="rule-title-input"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="مثال: احترام المنافسين والسلوك الرياضي"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-gold-500/50 text-sm text-white transition"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-white/70 mb-1 block">تفاصيل وشروط القاعدة</label>
            <textarea
              data-testid="rule-body-input"
              required
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="اكتب تفاصيل وشروط القاعدة بوضوح..."
              rows={4}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-gold-500/50 resize-none text-sm text-white transition"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-white/70 mb-1 block">ترتيب العرض</label>
            <input
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
              placeholder="0"
              className="w-32 bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold-500/50 text-sm text-white font-mono text-center"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-white/70 mb-1.5 block">صور توضيحية مرفقة (اختياري — كل صورة ≤2MB)</label>
            <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-black/40 border border-white/10 hover:border-gold-500/40 text-xs text-white font-bold cursor-pointer transition">
              <ImageIcon size={15} />
              <span>{form.images?.length ? `تم اختيار ${form.images.length} صورة` : "اختر صور مرفقة"}</span>
              <input data-testid="rule-image-input" type="file" accept="image/*" multiple onChange={onImages} className="hidden" />
            </label>

            {!!form.images?.length && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                {form.images.map((img, idx) => (
                  <div key={`${img.slice(0, 24)}-${idx}`} className="relative rounded-xl border border-white/10 overflow-hidden bg-black/40">
                    <img src={img} alt={`rule-${idx}`} className="h-24 w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImageAt(idx)}
                      className="absolute top-1.5 left-1.5 px-2 py-1 text-[10px] font-bold text-white bg-black/80 hover:bg-destructive rounded-lg transition"
                    >
                      حذف
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2.5 justify-end pt-2">
            <button
              type="button"
              onClick={() => { setEditing(null); setCreating(false); }}
              className="px-4 py-2.5 rounded-xl text-xs text-white/70 font-bold hover:bg-white/5 transition"
            >
              إلغاء
            </button>
            <button
              data-testid="save-rule"
              type="submit"
              className="px-5 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 flex items-center gap-1.5 text-xs shadow-md transition"
            >
              <Save size={14} /> حفظ القاعدة
            </button>
          </div>
        </form>
      )}

      {/* ── Rules List ── */}
      <div className="space-y-4">
        {rules.map((r, idx) => (
          <div key={r.id} data-testid={`rule-${r.id}`} className="bg-[#111113]/90 border border-white/10 rounded-3xl p-6 shadow-xl transition-all duration-300 hover:border-gold-500/30">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-2xl bg-gold-500/10 border border-gold-500/30 text-gold-500 grid place-items-center font-display font-black text-sm shrink-0 shadow-sm">
                {idx + 1}
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <h3 className="font-display font-black text-lg text-white">{r.title}</h3>
                <p className="text-sm text-white/70 whitespace-pre-wrap leading-relaxed">{r.body}</p>
                
                {(() => {
                  const imgs = Array.isArray(r.images) && r.images.length ? r.images : (r.image ? [r.image] : []);
                  if (!imgs.length) return null;
                  return (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {imgs.map((img, imageIdx) => (
                        <div
                          key={`${r.id}-${imageIdx}`}
                          className="relative group cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-black/40"
                          onClick={() => setSelectedImage(img)}
                        >
                          <img
                            src={img}
                            alt={`${r.title}-${imageIdx + 1}`}
                            className="rounded-2xl max-h-64 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                            data-testid={`rule-image-${r.id}-${imageIdx}`}
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Maximize2 className="text-white" size={22} />
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {isAdmin && (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => startEdit(r)} className="p-2 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition" title="تعديل">
                    <Edit size={16} />
                  </button>
                  <button onClick={() => remove(r.id)} className="p-2 rounded-xl text-destructive hover:bg-destructive/10 transition" title="حذف">
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {rules.length === 0 && (
          <div className="text-center text-white/40 py-16 bg-[#111113]/40 border border-white/5 rounded-3xl text-sm">
            لا توجد قوانين منشورة حالياً في المنصة
          </div>
        )}
      </div>

      {/* ── Lightbox Modal for Images ── */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white bg-black/50 rounded-full transition-colors"
              title="إغلاق"
            >
              <X size={24} />
            </button>
            <img
              src={selectedImage}
              alt="صورة مكبرة"
              className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl border border-white/10"
            />
          </div>
        </div>
      )}
    </div>
  );
}
